import { find, get, partial } from 'lodash';
import React, { PureComponent, ReactText } from 'react';

import Comment from './Comment';
import Event from './Event';
import { STREAM_TYPES } from './constants';

import type { FormVariables } from './types';
import type { Issue } from 'app/developer-v3/hooks/useIssue';

type StreamType = Issue['stream'][0]['dataType'];
type IssueStreamItem = Issue['stream'][0];

type Props = {
  accountOwnerEmail: string;
  appId: number;
  appImageUrl: string;
  appTitle: string;
  issue: Issue;
  isSaving: boolean;
  onSubmit: (variables: FormVariables) => void;
};

const removeUnusedEvents = issue => item => {
  if (item.dataType === STREAM_TYPES.COMMENTS) {
    return true;
  }

  const event = find(issue.events, { id: item.id });

  // TODO: This filtering should be happening on the server. We never show these events. (PDE-721)
  return event && event.contentObject && event.typeOf <= 6;
};

class Stream extends PureComponent<Props> {
  get filteredStream() {
    const { issue } = this.props;
    const stream = issue.stream || [];

    return stream.filter(removeUnusedEvents(issue));
  }

  getItemFromStream = (index: number) => this.filteredStream[index];

  getStreamItemDetails = (type: StreamType, id: string | ReactText) => {
    const items = get(this.props, ['issue', type], []);
    return items.find(item => item.id === id) || {};
  };

  getEvent = partial(this.getStreamItemDetails, STREAM_TYPES.EVENTS);
  getComment = partial(this.getStreamItemDetails, STREAM_TYPES.COMMENTS);

  itemIsDataType = (type: StreamType, index: number) => {
    const nextItem = this.getItemFromStream(index);

    if (!nextItem) {
      return false;
    }

    const itemDetail = this.getStreamItemDetails(
      nextItem.dataType,
      nextItem.id
    );

    // We only care about events that have typeOf <= 6
    if (itemDetail.typeOf && itemDetail.typeOf > 6) {
      return false;
    }

    return nextItem.dataType === type;
  };

  itemIsComment = partial(this.itemIsDataType, STREAM_TYPES.COMMENTS);
  itemIsEvent = partial(this.itemIsDataType, STREAM_TYPES.EVENTS);

  renderComment = (streamItem: IssueStreamItem, index: number) => {
    const {
      accountOwnerEmail,
      appId,
      appImageUrl,
      appTitle,
      isSaving,
      issue,
      onSubmit,
    } = this.props;
    const comment = this.getComment(streamItem.id);

    // Redundant check to make sure internal comments are only rendered
    // for @zapier email addresses
    const email = get(this.props, ['accountOwnerEmail'], '');
    if (!comment.isPublic && email.indexOf('@zapier.com') === -1) {
      return null;
    }

    // IssueDetailStream renders more of itself if it has children,
    // so when adding any items here, be sure to add them to the
    // IssueDetailComment.renderChildrenComments() method!
    return (
      <Comment
        accountOwnerEmail={accountOwnerEmail}
        appId={appId}
        appImageUrl={appImageUrl}
        appTitle={appTitle}
        comment={comment}
        depth={0}
        isFirstItem={index === 0}
        isSaving={isSaving}
        issueId={issue.id}
        key={'comment-' + comment.id}
        onSubmit={onSubmit}
        precedesEvent={this.itemIsEvent(index + 1)}
      />
    );
  };

  renderEvent = (streamItem: IssueStreamItem, index: number) => {
    const event = this.getEvent(streamItem.id);
    return (
      <Event
        event={event}
        followsComment={this.itemIsComment(index - 1) || index === 0}
        key={'event-' + event.id}
        precedesComment={
          this.itemIsComment(index + 1) ||
          index === this.props.issue.stream.length - 1
        }
      />
    );
  };

  getComponent = (streamItem: IssueStreamItem, index: number) => {
    const renderers = {
      comments: this.renderComment,
      events: this.renderEvent,
    };
    const renderStreamItem = renderers[streamItem.dataType];

    return renderStreamItem && renderStreamItem(streamItem, index);
  };

  render() {
    return (
      <div>
        {this.filteredStream.map((streamItem, i) => {
          return this.getComponent(streamItem, i);
        })}
      </div>
    );
  }
}

export default Stream;
